/**
 * One line of the Transaction History drawer.
 *
 * All of the per-ledger display logic lives here — the icon, the sign, the
 * units and the number formatting all vary by which ledger the row came from,
 * and keeping that in one component is what lets the card itself stay a shell.
 */

import React from "react";
import {FaClock, FaFire, FaGift, FaHistory} from "react-icons/fa";
import {RiVipDiamondLine} from "react-icons/ri";
import {MdCardGiftcard} from "react-icons/md";
import {BiPurchaseTag} from "react-icons/bi";
import {IoCheckmarkCircle, IoGameController} from "react-icons/io5";
import {TransactionRow, TransactionRowCategory} from "./transactionHistoryTypes";
import {formatDate, formatLargeNumber} from "./transactionHistoryFormat";

function getTransactionIcon(category: TransactionRowCategory) {
    switch (category) {
        case "quest":
            return <IoCheckmarkCircle className="transaction-icon quest"/>;
        case "time":
            return <FaClock className="transaction-icon time"/>;
        case "gaming":
            return <IoGameController className="transaction-icon gaming"/>;
        case "xp":
            return <RiVipDiamondLine className="transaction-icon xp"/>;
        case "reward":
            return <MdCardGiftcard className="transaction-icon reward"/>;
        case "fuel":
            return <FaFire className="transaction-icon fuel"/>;
        case "purchase":
            return <BiPurchaseTag className="transaction-icon purchase"/>;
        case "lootbox":
            return <FaGift className="transaction-icon lootbox"/>;
        default:
            return <FaHistory className="transaction-icon"/>;
    }
}

/**
 * Amount as the user should read it. Units differ per ledger:
 * orders are stored in CENTS, LootShop Credit in whole USD, everything else in
 * points. LootBox rows show no amount at all — the prize name IS the value.
 */
function formatAmount(transaction: TransactionRow): string {
    switch (transaction.type) {
        case "lootbox":
            return "";
        case "orders":
            return `$${((transaction.amount || 0) / 100).toFixed(2)}`;
        case "rewards":
            // Credit is USD, not a point balance — show "$1.00", not "1".
            return transaction.amountFormatted || `$${Number(transaction.amount || 0).toFixed(2)}`;
        default:
            return formatLargeNumber(transaction.amount || 0);
    }
}

function formatUnits(transaction: TransactionRow): string {
    if (transaction.type === "xp") {
        return " XP";
    }
    if (transaction.type === "fuel") {
        return " Fuel";
    }
    return "";
}

interface TransactionHistoryRowProps {
    transaction: TransactionRow;
}

const TransactionHistoryRow: React.FC<TransactionHistoryRowProps> = ({transaction}) => {
    /*
     * The two ledgers use OPPOSITE conventions, so the sign cannot be read off
     * `transactionType` alone.
     *
     * Fuel / XP (`transactions`): DEBIT = the user gains, CREDIT = the user
     * spends. LootShop Credit (`lootshopcredittransactions`, the "rewards"
     * rows): CREDIT = the user gains, DEBIT = the user spends — see
     * LootShopCreditTransaction in packages/models, and get_lootshop_credit.ts
     * which sums CREDIT minus DEBIT for the balance.
     *
     * Reading CREDIT as a deduction for both showed a $1 wheel win as "-1" in
     * red — the exact reward this filter was added to surface.
     */
    const isDeduction = transaction.type === "rewards"
        ? transaction.transactionType === "DEBIT"
        : transaction.transactionType === "CREDIT";

    // LootBox prize reveals have no signed amount to show.
    const sign = transaction.type === "lootbox" ? "" : isDeduction ? "-" : "+";

    return (
        <div className="transaction-history-card__item">
            {getTransactionIcon(transaction.category)}
            <div className="transaction-history-card__info">
                <span className="transaction-history-card__description">
                    {transaction.description}
                </span>
                <span className="transaction-history-card__date">
                    {formatDate(transaction.createdAt)}
                </span>
            </div>
            <span className={`transaction-history-card__amount ${isDeduction ? "negative" : "positive"}`}>
                {sign}
                {formatAmount(transaction)}
                {formatUnits(transaction)}
            </span>
        </div>
    );
};

export default TransactionHistoryRow;
